/**
 * [TD-47.2a + TD-47.4] AuctionPlayerPool - format wrapper around the
 * format-agnostic `PlayerPoolWithDrawer` core. Mirrors `AuctionChatPanel`
 * from 2c.6-C.
 *
 * Owns:
 *   • Player-list fetch (auction's `/api/players?available_only=false`)
 *   • `useDraft()` context wiring (nominate, wsUser, currentNomination)
 *   • Auto-filter on new nomination's position (auction Section 8 behavior)
 *   • Mounting `AuctionActionDrawer` as the drawer slot
 *   • Single-click commit (Gap 1 verdict B preserved)
 *
 * v1.2-TD-47.4 deltas (Drafted Player Visibility):
 *   • Gap 2 verdict C - zero backend change. Flips `available_only: false`
 *     so the API returns ALL players. Bumps `limit: 500` to give headroom
 *     over the ~198-player seeded pool.
 *   • Gap 9 verdict - decorates each player with `is_drafted` derived from
 *     `Object.values(draftState.managers).flatMap(m => m.roster)`.
 *   • Gap 8 verdict A - passes `getDraftedLabel(player) → "Sold ${cost} to
 *     {team_name}"` so the dumb-core renders the format-specific copy
 *     beneath the player name.
 *   • Gap 6 verdict A - owns `analysisPlayer` state + mounts
 *     <PlayerAnalysisModal>; PlayerPoolWithDrawer fires `onDraftedRowClick`.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { authHeaders, useAuth } from "../../contexts/AuthContext";
import { useDraft } from "../../contexts/DraftContext";
import { useTheme } from "../../contexts/ThemeContext";
import { useDraftPoolFetch } from "../../hooks/useDraftPoolFetch";
import PlayerPoolWithDrawer from "./PlayerPoolWithDrawer";
import AuctionActionDrawer from "./AuctionActionDrawer";
import PlayerAnalysisModal from "./PlayerAnalysisModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AuctionPlayerPool({ draftState, leagueId, onCanvasUpdate, leagueRoster = null }) {
  const { user } = useAuth();
  const { nominate, wsUser } = useDraft();
  const { isDark } = useTheme();

  const myId = wsUser?.id || user?.id;
  const isNomination = draftState?.state === "NOMINATION";
  const isMyTurnToNominate =
    isNomination && draftState?.current_nominator_id === myId;

  const [players, setPlayers] = useState([]);
  // Fetch signal: useDraftPoolFetch owns the /api/players fetch with bounded
  // retry + visible error state. We forward its player list into local
  // `players` state so the existing decorate/enrichment flow is untouched.
  const {
    players: fetchedPlayers,
    loadError,
    loading: poolLoading,
    retry: retryPoolFetch,
  } = useDraftPoolFetch(leagueId);
  useEffect(() => { setPlayers(fetchedPlayers); }, [fetchedPlayers]);
  const [selected, setSelected] = useState(null);
  const [openingBid, setOpeningBid] = useState(1);
  // [TD-47.4 Gap 6] Modal state owned at the wrapper, not the dumb-core.
  const [analysisPlayer, setAnalysisPlayer] = useState(null);
  // [Sprint §Part 1] Hydrate ADP + PGF rank + composite badge onto every
  // pool player from the analytics/edge/{sleepers,overvalued,watchlist}
  // endpoints. Unions cover the top-N per-position pool + all
  // divergent-flag players; the remainder show without ADP/PGF, sorted
  // by ADP fallback (which pushes them to the tail).
  const [pgfEnrichment, setPgfEnrichment] = useState({});
  useEffect(() => {
    if (!leagueId) return;
    let alive = true;
    const sf = draftState?.scoring_format || "half_ppr";
    const paramsBase = new URLSearchParams({
      league_id: leagueId, season_year: "2025", scoring_format: sf,
    });
    const fetchOne = (path, extra = "") =>
      axios.get(
        `${API}/analytics/edge/${path}?${paramsBase.toString()}${extra}`,
        { headers: authHeaders() },
      ).then((r) => r.data).catch(() => null);
    (async () => {
      const [finds, overvalued, watchlist] = await Promise.all([
        fetchOne("finds", "&limit=500"),
        fetchOne("overvalued", "&limit=500"),
        fetchOne("watchlist", "&min_edge_pct=0.0"),
      ]);
      if (!alive) return;
      const merge = {};
      const eat = (rows) => {
        (rows || []).forEach((r) => {
          const cur = merge[r.player_id] || {};
          merge[r.player_id] = {
            adp_rank_position: r.adp_rank_position ?? cur.adp_rank_position,
            pgf_rank_position: r.pgf_rank_position ?? cur.pgf_rank_position,
            pgf_composite_score: r.pgf_composite_score ?? cur.pgf_composite_score
              ?? (typeof r.driving_axis_score === "number" ? null : null),
            insufficient_data: r.badge === "INSUFFICIENT DATA",
          };
        });
      };
      eat(finds?.finds);
      eat(overvalued?.overvalued);
      eat(watchlist?.watchlist);
      setPgfEnrichment(merge);
    })();
    return () => { alive = false; };
  }, [leagueId, draftState?.scoring_format]);

  // [Egress fix 2026-07-31] The pool fetch used to run keyed on
  // `draftState?.completed_sales?.length`, causing a full /api/players
  // refetch (limit 2500) on every sale per client. Availability is
  // already derived client-side from draftState.managers via
  // draftedById below, and DraftContext calls setDraftState on every
  // SALE_COMPLETE / SALE_UNDONE / STATE_SNAPSHOT. Static pool never
  // changes mid-draft. Fetch moved into useDraftPoolFetch (keyed on
  // leagueId only) with bounded auto-retry + visible error state so a
  // failed initial load does NOT silently strand a manager for the
  // whole draft.

  // [TD-47.4 Gap 9] Build draftedById from managers' rosters (canonical
  // source - completed_sales is sliding-window trimmed). Keyed by player.id;
  // value carries everything the inline label + modal need.
  const draftedById = useMemo(() => {
    const map = {};
    const managers = draftState?.managers || {};
    Object.entries(managers).forEach(([teamId, mgr]) => {
      (mgr?.roster || []).forEach((row) => {
        const pid = row?.player?.id;
        if (!pid) return;
        map[pid] = {
          team_id: teamId,
          team_name: mgr?.team_name || "Team",
          paid: row?.paid ?? 0,
        };
      });
    });
    return map;
  }, [draftState?.managers, draftState?.completed_sales?.length]);

  // [TD-47.4 Gap 9] Decorate the player list with is_drafted before
  // handing to the dumb-core. O(n); n ≤ 500.
  const decoratedPlayers = useMemo(
    () =>
      players.map((p) => {
        const enrich = pgfEnrichment[p.id];
        const withDrafted = draftedById[p.id] ? { ...p, is_drafted: true } : p;
        return enrich ? { ...withDrafted, ...enrich } : withDrafted;
      }),
    [players, draftedById, pgfEnrichment],
  );

  // [TD-47.4 Gap 8] Format-specific drafted-row label.
  const getDraftedLabel = (player) => {
    const ev = draftedById[player.id];
    if (!ev) return null;
    return `Sold $${ev.paid} to ${ev.team_name}`;
  };

  // [Gap 1 verdict B] Single-click commit. No confirmation step.
  const handleNominate = () => {
    if (!selected) return;
    nominate(selected.id, openingBid);
    setSelected(null);
    setOpeningBid(1);
  };

  const handleCancel = () => {
    setSelected(null);
    setOpeningBid(1);
  };

  // Reset opening bid whenever the user picks a different player.
  useEffect(() => {
    if (selected) setOpeningBid(1);
  }, [selected?.id]);

  const onClockBanner =
    isMyTurnToNominate && !selected && !draftState?.current_nomination ? (
      <div
        className="px-3 py-2 border-b border-[#FF6B00]/30 shrink-0 text-center animate-orange-pulse"
        style={{ background: "rgba(255,107,0,0.08)" }}
        data-testid="auction-pool-on-clock-banner"
      >
        <p className="font-display font-black text-xs uppercase tracking-wider text-[#FF6B00]">
          YOU'RE ON THE CLOCK - tap a player to nominate
        </p>
      </div>
    ) : null;

  return (
    <>
      {loadError && (
        <div
          className="mb-3 px-4 py-3 rounded-lg bg-red-500/10 border border-red-500/40 text-red-400 text-sm flex items-center justify-between"
          data-testid="auction-pool-load-error"
        >
          <span>Player pool failed to load. {loadError}</span>
          <button
            type="button"
            onClick={retryPoolFetch}
            className="px-3 py-1 rounded bg-red-500/20 hover:bg-red-500/30 text-xs font-display font-bold uppercase tracking-wide"
            data-testid="auction-pool-retry"
          >Retry</button>
        </div>
      )}
      <PlayerPoolWithDrawer
        players={decoratedPlayers}
        isMyTurn={isMyTurnToNominate}
        onClockBanner={onClockBanner}
        selected={selected}
        setSelected={setSelected}
        autoPositionFilter={draftState?.current_nomination?.player?.position || null}
        testIdPrefix="auction-pool"
        leagueRoster={leagueRoster}
        onDraftedRowClick={setAnalysisPlayer}
        onCanvasUpdate={onCanvasUpdate}
        getDraftedLabel={getDraftedLabel}
        renderDrawer={(player) => (
          <AuctionActionDrawer
            player={player}
            openingBid={openingBid}
            setOpeningBid={setOpeningBid}
            onNominate={handleNominate}
            onCancel={handleCancel}
          />
        )}
      />
      {analysisPlayer && (
        <PlayerAnalysisModal
          leagueId={leagueId}
          player={analysisPlayer}
          onClose={() => setAnalysisPlayer(null)}
          isDark={isDark}
          draftEpoch={draftState?.completed_sales?.length || 0}
        />
      )}
    </>
  );
}
